<template>
  <v-app class="application">
    <vetalfa-support-chat></vetalfa-support-chat>
    <alert></alert>
    <loading></loading>
    <confirm></confirm>
    <vetalfa-purchases v-if="!!user"></vetalfa-purchases>
    <v-content
      v-if="loadContent"
      style="overflow: hidden !important"
      :class="$route.meta.backgroundColor || 'white'"
    >
      <v-row justify="center" class="ma-0 pa-0 vetalfa-row">
        <router-view></router-view>
      </v-row>
    </v-content>
    <input type="text" class="copy-to-clipboard" id="copy-to-clipboard" />
    <nav
      v-if="displayBottomBar"
      class="app-bottom-bar white"
      aria-label="Navegação principal"
    >
      <template v-for="(item, index) in visibleItems">
        <!-- Botão de IA em destaque, na posição central -->
        <div
          v-if="index === 2 && showAiButton"
          :key="`bottom-nav-ai-${item.id}`"
          class="app-bottom-bar-ai vetalfa-pointer"
          role="button"
          aria-label="Assistente de IA"
          @click="openAiConversations"
        >
          <v-icon size="22" color="white">{{ mdiCreation }}</v-icon>
        </div>
        <v-avatar
          :key="`bottom-nav-${item.id}`"
          class="text-none primary--text py-1 vetalfa-pointer"
          elevation="0"
          size="52"
          :color="isActive(item) ? 'rgb(0, 114, 138, 0.14)' : 'transparent'"
          active-class="transparent"
          :aria-label="item.text"
          @click="selectItem(item)"
        >
          <v-icon
            size="24"
            class="mb-1"
            :color="isActive(item) ? 'primary' : 'grey darken-1'"
            >{{ item.icon }}</v-icon
          >
        </v-avatar>
      </template>
      <!-- Fallback quando há menos de 3 itens antes da posição central -->
      <div
        v-if="showAiButton && visibleItems.length < 3"
        :key="'bottom-nav-ai-end'"
        class="app-bottom-bar-ai vetalfa-pointer"
        role="button"
        aria-label="Assistente de IA"
        @click="openAiConversations"
      >
        <v-icon size="22" color="white">{{ mdiCreation }}</v-icon>
      </div>
    </nav>
    <vetalfa-all-sections-panel
      v-if="!!user && sectionsPanelMounted"
      v-model="sectionsPanel"
    ></vetalfa-all-sections-panel>
  </v-app>
</template>

<style>
@import "./vetalfa.css";
.app-bottom-bar {
  border-radius: 32px;
  border-bottom-left-radius: 32px !important;
  border-bottom-right-radius: 32px !important;
  padding: 4px 4px 4px 4px;
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  border: 1px solid #eeeeee !important;
  box-shadow: 0px 0px 3px rgba(0, 0, 0, 0.16);
  /* Largura por conteúdo: dispensa o cálculo em JS com retry. */
  display: flex;
  align-items: center;
  gap: 8px;
  width: -webkit-fit-content;
  width: fit-content;
  max-width: calc(100% - 16px);
}
.app-bottom-bar-ai {
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #193153 0%, #00728a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 114, 138, 0.4);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  margin: 0 2px;
}
.app-bottom-bar-ai:hover {
  transform: scale(1.08);
  box-shadow: 0 4px 12px rgba(0, 114, 138, 0.5);
}
</style>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import messages from "@/constants/messages.js";
import Loading from "@/components/Loading.vue";
import Alert from "@/components/Alert.vue";
import Confirm from "@/components/Confirm.vue";
import PurchasesComponent from "@/components/Purchases.vue";
import {
  mdiHomeOutline,
  mdiMagnify,
  mdiPrescription,
  mdiCreation,
  mdiViewGridOutline
} from "@mdi/js";

/**
 * Itens da barra inferior. Cada um conhece a própria rota ou ação, para o
 * item ativo e a navegação não dependerem de índice posicional.
 */
const NAV_ITEMS = [
  { id: "home", text: "Início", icon: mdiHomeOutline, route: "/home" },
  { id: "search", text: "Buscar", icon: mdiMagnify, route: "/items/searches" },
  {
    id: "rx",
    text: "Prescrição",
    icon: mdiPrescription,
    route: "/prescriptions",
    toggle: "prescriptions"
  },
  {
    id: "sections",
    text: "Todas as seções",
    icon: mdiViewGridOutline,
    action: "openSectionsPanel"
  }
];

const NAV_ROUTES = NAV_ITEMS.filter(i => i.route).map(i => i.route);
const NAV_DEBOUNCE = 300;

export default {
  name: "App",
  components: {
    "vetalfa-support-chat": () => import("@/components/SupportChat.vue"),
    loading: Loading,
    alert: Alert,
    confirm: Confirm,
    "vetalfa-purchases": PurchasesComponent,
    // Fora do primeiro paint: só carrega quando o usuário abre o painel.
    "vetalfa-all-sections-panel": () =>
      import(
        /* webpackChunkName: "vetalfa_all_sections_panel" */ "@/components/AllSectionsPanel.vue"
      )
  },
  computed: {
    ...mapGetters("general", ["user", "toggles", "sectionsPanelOpen"]),
    ...mapGetters("drugs", ["searchValue"]),
    ...mapGetters("prescriptions", ["prescription"]),
    showAiButton() {
      return !!(this.toggles && this.toggles["ai_search"]);
    },
    visibleItems() {
      return NAV_ITEMS.filter(
        item => !item.toggle || (this.toggles && this.toggles[item.toggle])
      );
    },
    displayBottomBar() {
      const path = this.$route.path;
      if (!NAV_ROUTES.includes(path)) return false;
      if (path === "/prescriptions" && !!this.prescription && !!this.prescription.length) return false;
      return !(path === "/items/searches" && !!this.searchValue);
    },
    sectionsPanel: {
      get() {
        return this.sectionsPanelOpen;
      },
      set(value) {
        this.setSectionsPanel(value);
      }
    }
  },
  data: () => ({
    loadContent: false,
    navigating: false,
    sectionsPanelMounted: false,
    mdiCreation
  }),
  methods: {
    ...mapActions("general", [
      "partnersRedirect",
      "pushOrPopRouteOfBreadcrumb",
      "routePathRedirect",
      "routeBack",
      "logEvent"
    ]),
    ...mapMutations("general", ["setSectionsPanel"]),
    /** Todas as seções fica ativo enquanto o painel está aberto. */
    isActive(item) {
      if (item.id === "sections") return this.sectionsPanelOpen;
      if (this.sectionsPanelOpen) return false;
      return item.route === this.$route.path;
    },
    openAiConversations() {
      this.setSectionsPanel(false);
      this.routePathRedirect({ path: "/ai-conversations" });
    },
    selectItem(item) {
      if (this.navigating) return;
      this.navigating = true;
      setTimeout(() => {
        this.navigating = false;
      }, NAV_DEBOUNCE);

      this.logEvent({ name: "bottom_nav_click", object: { id: item.id } });

      if (item.action === "openSectionsPanel") {
        this.logEvent("home_all_sections_open");
        this.sectionsPanelMounted = true;
        this.setSectionsPanel(true);
        return;
      }

      this.setSectionsPanel(false);
      if (item.route === this.$route.path) return;

      if (item.id === "home") {
        return this.routeBack();
      }
      if (this.$route.path === "/home") {
        return this.routePathRedirect({ path: item.route });
      }
      this.pushOrPopRouteOfBreadcrumb({ offset: 1 });
      this.$router.replace(item.route);
    }
  },
  watch: {
    sectionsPanelOpen(open) {
      if (open) this.sectionsPanelMounted = true;
    },
    "$route.path"() {
      if (this.sectionsPanelOpen) {
        this.setSectionsPanel(false);
      }
    }
  },
  created: async function() {
    try {
      if (this._isCordovaApp()) {
        this.loadContent = true;
      } else if (typeof InstallTrigger !== "undefined") {
        if (process.env.VUE_APP_ENV === "partners") {
          throw new Error();
        }
        alert(messages.FIREFOX_SUPPORT);
      } else {
        throw new Error();
      }
    } catch {
      if (process.env.VUE_APP_ENV === "partners") {
        this.loadContent = await this.partnersRedirect();
      } else {
        this.loadContent = true;
      }
    }
  }
};
</script>
